/* ──────────────────────────────────────────────────────────────────────
 * PipelineBars — animated horizontal stage bars + mini funnel SVG
 *
 * Replaces the flat per-stage progress bars in the Pipeline panel. Each
 * stage row gets a 6px rounded-end bar that fills left-to-right on
 * mount with a 80ms stagger. To the right: count + a tiny "+N" sage
 * delta when the stage is growing this week (driven by mock data —
 * caller can pass real deltas later).
 *
 * Below the rows: a mini funnel — 6 trapezoidal SVG shapes stepping
 * down with conversion % labels between adjacent steps.
 * ──────────────────────────────────────────────────────────────────── */

import React from "react";
import { Box, Typography, Stack } from "@mui/material";

const INK         = "#0F172A";
const SLATE       = "#64748b";
const SAGE        = "#7A9B76";
const HAIRLINE    = "rgba(10,14,26,0.06)";
const FONT_SERIF  = `'Fraunces', 'GT Sectra', Georgia, serif`;
const FONT_MONO   = `'JetBrains Mono', 'SF Mono', Menlo, monospace`;

export interface PipelineStage {
  key: string;
  label: string;
  count: number;
  color: string;
  /** Optional weekly delta (positive = growing this week). */
  delta?: number;
}

interface Props { stages: PipelineStage[]; }

export default function PipelineBars({ stages }: Props) {
  const total = stages.reduce((s, x) => s + x.count, 0);
  const maxCount = Math.max(1, ...stages.map((s) => s.count));

  return (
    <Box>
      <Stack spacing={1.4}>
        {stages.map((s, i) => {
          const pctOfMax = (s.count / maxCount) * 100;
          const pctOfTotal = total > 0 ? Math.round((s.count / total) * 100) : 0;
          return (
            <Box key={s.key}>
              <Stack direction="row" alignItems="baseline" justifyContent="space-between" mb={0.4}>
                <Stack direction="row" alignItems="center" gap={0.8}>
                  <Box sx={{
                    width: 6, height: 6, borderRadius: "50%",
                    bgcolor: s.color, flexShrink: 0,
                    boxShadow: s.count > 0 ? `0 0 5px ${s.color}66` : "none",
                  }} />
                  <Typography sx={{ fontSize: 11.5, fontWeight: 500, color: INK, letterSpacing: "-0.005em" }}>
                    {s.label}
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="baseline" gap={0.6}>
                  {s.delta !== undefined && s.delta > 0 && (
                    <Typography sx={{
                      fontFamily: FONT_MONO, fontSize: 9.5,
                      color: SAGE, fontWeight: 600,
                      letterSpacing: "0.02em",
                    }}>
                      ↗ +{s.delta}
                    </Typography>
                  )}
                  <Typography sx={{
                    fontFamily: FONT_SERIF, fontSize: 14, fontWeight: 400,
                    color: INK, lineHeight: 1, fontVariantNumeric: "tabular-nums",
                  }}>
                    {s.count}
                  </Typography>
                  <Typography sx={{
                    fontSize: 9, fontWeight: 500, color: SLATE,
                    letterSpacing: "0.05em",
                    minWidth: 26, textAlign: "right",
                  }}>
                    {pctOfTotal}%
                  </Typography>
                </Stack>
              </Stack>
              {/* Animated bar — width animates from 0% to pctOfMax%
                  on mount with a staggered delay so the rows cascade. */}
              <Box sx={{
                height: 6, borderRadius: 999,
                bgcolor: "rgba(10,14,26,0.04)",
                overflow: "hidden",
              }}>
                <Box sx={{
                  height: "100%",
                  width: `${pctOfMax}%`,
                  bgcolor: s.color,
                  borderRadius: 999,
                  transformOrigin: "left center",
                  animation: `bar-fill-${i} 600ms ${80 + i * 80}ms cubic-bezier(0.16, 1, 0.3, 1) both`,
                  [`@keyframes bar-fill-${i}` as any]: {
                    from: { transform: "scaleX(0)" },
                    to:   { transform: "scaleX(1)" },
                  },
                }} />
              </Box>
            </Box>
          );
        })}
      </Stack>

      {/* ── Mini funnel — trapezoids stepping down with conversion % */}
      {stages.length >= 2 && total > 0 && (
        <Box mt={3}>
          <Typography sx={{
            fontSize: 9, fontWeight: 600, color: SLATE,
            letterSpacing: "0.22em", textTransform: "uppercase",
            mb: 1,
          }}>
            Conversion Funnel
          </Typography>
          <MiniFunnel stages={stages} />
        </Box>
      )}
    </Box>
  );
}

/* ── MiniFunnel — pure inline SVG ────────────────────────────────── */
function MiniFunnel({ stages }: { stages: PipelineStage[] }) {
  // Drop the "lost" stage from the funnel — funnels read better as a
  // forward conversion path.
  const path = stages.filter((s) => s.key !== "lost");
  if (path.length < 2) return null;

  const W = 240;
  const H = 64;
  const top = path[0].count;
  const segments = path.length;
  const segW = W / segments;

  // Each trapezoid sits inside its segW slot. Its top width = scaled
  // by count/top, its bottom width = scaled by next.count/top.
  return (
    <Box component="svg" viewBox={`0 0 ${W} ${H + 28}`} sx={{ width: "100%", height: H + 28, display: "block" }}>
      {path.map((s, i) => {
        if (i === path.length - 1) return null;
        const next = path[i + 1];
        const topR = top > 0 ? s.count / top : 0;
        const botR = top > 0 ? next.count / top : 0;
        const xLeft  = i * segW;
        const xRight = (i + 1) * segW;
        const topPad = (segW * (1 - topR)) / 2;
        const botPad = (segW * (1 - botR)) / 2;
        const trapX1 = xLeft + topPad;
        const trapX2 = xRight - topPad;
        const trapX3 = xRight - botPad;
        const trapX4 = xLeft + botPad;
        const convPct = s.count > 0 ? Math.round((next.count / s.count) * 100) : 0;

        return (
          <g key={s.key}>
            <path
              d={`M ${trapX1.toFixed(1)} 4 L ${trapX2.toFixed(1)} 4 L ${trapX3.toFixed(1)} ${H} L ${trapX4.toFixed(1)} ${H} Z`}
              fill={s.color}
              opacity={0.85}
              style={{
                transformOrigin: `${((trapX1 + trapX2) / 2).toFixed(1)}px 4px`,
                animation: `funnel-step-${i} 500ms ${100 + i * 80}ms cubic-bezier(0.16, 1, 0.3, 1) both`,
                [`@keyframes funnel-step-${i}` as any]: {
                  from: { transform: "scaleY(0)", opacity: 0 },
                  to:   { transform: "scaleY(1)", opacity: 0.85 },
                },
              }}
            />
            {/* Conversion % label between segments */}
            <text
              x={trapX2.toFixed(1)}
              y={H + 14}
              textAnchor="middle"
              style={{
                fontFamily: FONT_MONO,
                fontSize: 8.5,
                fontWeight: 600,
                fill: SLATE,
                letterSpacing: "0.08em",
              }}
            >
              {convPct}% →
            </text>
          </g>
        );
      })}
      {/* Stage micro-labels along the top */}
      {path.map((s, i) => (
        <text
          key={`lab-${s.key}`}
          x={(i * segW + segW / 2).toFixed(1)}
          y={H + 26}
          textAnchor="middle"
          style={{
            fontFamily: FONT_MONO,
            fontSize: 7.5,
            fontWeight: 500,
            fill: i === 0 ? INK : "transparent",
            letterSpacing: "0.08em",
          }}
        >
          {s.label.toUpperCase()}
        </text>
      ))}
    </Box>
  );
}
